import { Box, Divider } from '@mui/material';
import { memo, ReactElement, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultErrorScreen, DefaultLoadingScreen } from '../ApiLoadingGuard';
import { TrainAuxImagesCarousel } from './TrainAuxImagesCarousel';
import { UseQueryResult } from '@tanstack/react-query';
import { ApiError } from '../ApiLoadingGuard/ApiLoadingGuard';
import { TrainAuxCapturesData } from '../../api/queries/trainAuxCaptures';

export interface TrainAuxImagesCardQueryGuardedProps {
  auxCapturesQueryData: UseQueryResult<TrainAuxCapturesData, ApiError>;
  jobsiteId: string;
  recordingId: string;
}

export function TrainAuxImagesCardQueryGuarded({
  auxCapturesQueryData,
  jobsiteId,
  recordingId,
}: TrainAuxImagesCardQueryGuardedProps) {
  const { isPending, isError, data, isEnabled } = auxCapturesQueryData;

  const isNotAvailable = isError || !isEnabled;

  if (isNotAvailable) {
    return <DefaultErrorScreen defaultText='recording.auxiliary.noImagesAvailable' />;
  }

  if (isPending) {
    return <DefaultLoadingScreen />;
  }

  if (data) {
    return <TrainAuxImagesCard auxCapturesData={data} jobsiteId={jobsiteId} recordingId={recordingId} />;
  }
}

interface TrainAuxImagesCardProps {
  auxCapturesData: TrainAuxCapturesData;
  jobsiteId: string;
  recordingId: string;
}

export const TrainAuxImagesCard = memo(function ({
  auxCapturesData,
  jobsiteId,
  recordingId,
}: TrainAuxImagesCardProps): ReactElement {
  const { t } = useTranslation();
  const { auxCaptures } = auxCapturesData;

  const sortedCaptures = [...auxCaptures].sort((a, b) => b.auxIndex - a.auxIndex);

  if (sortedCaptures.length === 0) {
    return (
      <Box display='flex' alignItems='center' justifyContent='center' height='100%' color='text.secondary'>
        {t('recording.auxiliary.noImagesAvailable')}
      </Box>
    );
  }

  return (
    <Box display='flex' flexDirection='row' padding={2}>
      {sortedCaptures.map((auxCapture, index) => (
        <Fragment key={auxCapture.auxIndex}>
          <Box display='flex' flexDirection='column' flex={1}>
            <TrainAuxImagesCarousel auxCapture={auxCapture} jobsiteId={jobsiteId} recordingId={recordingId} />
          </Box>
          {index < sortedCaptures.length - 1 && <Divider orientation='vertical' flexItem sx={{ marginX: 1 }} />}
        </Fragment>
      ))}
    </Box>
  );
});
